/**
 * DRC administrative reference data for the Ebola situation-room dashboard.
 *
 * Mirrors DRC_PROVINCES / DRC_SITE_TO_ZONE / DRC_ZONE_STRAIN in
 * backend/app/api/reports.py — keep both lists in sync by convention since
 * traveler_screenings has no schema to enforce it (province/health_zone are
 * derived from entry_city on the backend, not real columns).
 */

export const DRC_PROVINCES: Record<string, string[]> = {
  "Ituri": ["Bunia", "Mambasa", "Mahagi"],
  "Nord-Kivu": ["Goma", "Beni", "Butembo", "Rutshuru"],
  "Sud-Kivu": ["Uvira"],
  "Kasaï": ["Tshikapa"],
  "Équateur": ["Mbandaka"],
};

export interface DrcSiteInfo {
  province: string;
  healthZone: string;
  type: "point_entree" | "zone_sante";
}

export const DRC_SITE_TO_ZONE: Record<string, DrcSiteInfo> = {
  "Aéroport de Goma": { province: "Nord-Kivu", healthZone: "Goma", type: "point_entree" },
  "Poste-frontière de Kasindi (Beni)": { province: "Nord-Kivu", healthZone: "Beni", type: "point_entree" },
  "Poste-frontière de Bunagana": { province: "Nord-Kivu", healthZone: "Rutshuru", type: "point_entree" },
  "Port de Kalundu (Uvira)": { province: "Sud-Kivu", healthZone: "Uvira", type: "point_entree" },
  "Bunia": { province: "Ituri", healthZone: "Bunia", type: "zone_sante" },
  "Mambasa": { province: "Ituri", healthZone: "Mambasa", type: "zone_sante" },
  "Mahagi": { province: "Ituri", healthZone: "Mahagi", type: "zone_sante" },
  "Goma": { province: "Nord-Kivu", healthZone: "Goma", type: "zone_sante" },
  "Beni": { province: "Nord-Kivu", healthZone: "Beni", type: "zone_sante" },
  "Butembo": { province: "Nord-Kivu", healthZone: "Butembo", type: "zone_sante" },
  "Tshikapa": { province: "Kasaï", healthZone: "Tshikapa", type: "zone_sante" },
  "Mbandaka": { province: "Équateur", healthZone: "Mbandaka", type: "zone_sante" },
};

export const DRC_POINTS_OF_ENTRY = Object.entries(DRC_SITE_TO_ZONE)
  .filter(([, v]) => v.type === "point_entree")
  .map(([name]) => name);

// Operator-configured, NOT detected per-patient data — placeholder until INRB typing feeds real per-zone results.
export const DRC_ZONE_STRAIN: Record<string, "zaire" | "bundibugyo" | "unconfirmed"> = {
  Bunia: "zaire",
  Mambasa: "unconfirmed",
  Goma: "zaire",
  Beni: "bundibugyo",
};

interface ProvinceGeo {
  lat: number;
  lng: number;
  bounds: [[number, number], [number, number]];
}

export const DRC_PROVINCE_CENTROIDS: Record<string, ProvinceGeo> = {
  "Ituri": { lat: 1.87, lng: 29.9, bounds: [[0.0, 27.5], [3.8, 31.5]] },
  "Nord-Kivu": { lat: -0.7, lng: 29.2, bounds: [[-2.2, 27.5], [1.0, 30.5]] },
  "Sud-Kivu": { lat: -3.0, lng: 28.0, bounds: [[-4.8, 26.5], [-1.5, 29.5]] },
  "Kasaï": { lat: -6.0, lng: 21.0, bounds: [[-7.5, 19.5], [-4.5, 23.0]] },
  "Équateur": { lat: 0.05, lng: 18.3, bounds: [[-2.0, 16.0], [2.5, 21.0]] },
};

export const DRC_CENTER: [number, number] = [-2.9, 23.6];
export const DRC_BOUNDS: [[number, number], [number, number]] = [[-13.5, 12.2], [5.4, 31.5]];
